import React, { useState, useEffect } from 'react';
import MultiSelectDropdown from './multi-select';
import { ContractorService } from '../services/contractor.service';
import { GeneralContractorService } from '../services/general-contractor.service';
import { lookupService } from '../services/lookups.service';
import Accordion from './Accordion';

interface FilterOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onClearAll: () => void;
  activeFilters: {
    status: string[];
    contractor: string[];
    mechanicalContractor: string[];
    localUnion: string[];
    organizer: string[];
    sector: string[];
    trade: string[];
    workersType: string[];
    province: string[];
    search: string;
  };
  onApply: (filters: {
    status: string[];
    contractor: string[];
    mechanicalContractor: string[];
    localUnion: string[];
    organizer: string[];
    sector: string[];
    trade: string[];
    workersType: string[];
    province: string[];
    search: string;
  }) => void;
}

interface Option {
  id: string;
  name: string;
}

const FilterOverlay: React.FC<FilterOverlayProps> = ({
  isOpen,
  onClose,
  onClearAll,
  activeFilters,
  onApply,
}) => {
  // Add state for expanded sections
  const [expandedSections, setExpandedSections] = useState({
    status: false,
    contractor: false,
    mechanicalContractor: false,
    localUnion: false,
    organizer: false,
    sector: false,
    trade: false,
    workersType: false,
    province: false,
  });

  // Add state for status filters
  const [selectedStatus, setSelectedStatus] = useState<string[]>(activeFilters.status || []);

  // Add state for provinces
  const [provinces, setProvinces] = useState<Option[]>([]);
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>(activeFilters.province || []);

  // Add state for all sections
  const [contractors, setContractors] = useState<Option[]>([]);
  const [selectedContractors, setSelectedContractors] = useState<string[]>(activeFilters.contractor || []);

  const [mechanicalContractors, setMechanicalContractors] = useState<Option[]>([]);
  const [selectedMechanicalContractors, setSelectedMechanicalContractors] = useState<string[]>(activeFilters.mechanicalContractor || []);

  const [localUnions, setLocalUnions] = useState<Option[]>([]);
  const [selectedLocalUnions, setSelectedLocalUnions] = useState<string[]>(activeFilters.localUnion || []);

  const [organizers, setOrganizers] = useState<Option[]>([]);
  const [selectedOrganizers, setSelectedOrganizers] = useState<string[]>(activeFilters.organizer || []);

  const [sectors, setSectors] = useState<Option[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>(activeFilters.sector || []);

  const [trades, setTrades] = useState<Option[]>([]);
  const [selectedTrades, setSelectedTrades] = useState<string[]>(activeFilters.trade || []);

  // Add state for names (except contractors and sectors)

  // Add state for workers type
  const [selectedWorkersType, setSelectedWorkersType] = useState<string[]>(activeFilters.workersType || []);

  // Fetch general contractors (only those with jobsites)
  useEffect(() => {
    const fetchGeneralContractors = async () => {
      try {
        const contractorsData = await GeneralContractorService.getGeneralContractorsFromJobsites();
        const formattedContractors = contractorsData.map(contractor => ({
          id: contractor.id.toString(),
          name: contractor.name
        }));
        // Add "No General Contractor" option at the beginning
        setContractors([
          { id: 'blank', name: 'No General Contractor' },
          ...formattedContractors
        ]);
      } catch (error) {
        console.error('Error fetching general contractors:', error);
      }
    };

    fetchGeneralContractors();
  }, []);

  // Fetch mechanical contractors (only those seen in jobsite visits)
  useEffect(() => {
    const fetchMechanicalContractors = async () => {
      try {
        const contractorsData = await ContractorService.getMechanicalContractorsFromVisits();
        const formattedMechanicalContractors = contractorsData.map((contractor: any) => ({
          id: contractor.id.toString(),
          name: contractor.name
        }));
        setMechanicalContractors(formattedMechanicalContractors);
      } catch (error) {
        console.error('Error fetching mechanical contractors:', error);
      }
    };

    fetchMechanicalContractors();
  }, []);

  // Fetch local unions (only those seen in jobsite visits)
  useEffect(() => {
    const fetchLocalUnions = async () => {
      try {
        const localUnionsData = await lookupService.getLocalUnionsFromVisits();
        const formattedLocalUnions = localUnionsData.map((localUnion: any) => ({
          id: localUnion.id.toString(),
          name: `Local ${localUnion.unionNumber}`
        }));
        setLocalUnions(formattedLocalUnions);
      } catch (error) {
        console.error('Error fetching local unions:', error);
      }
    };

    fetchLocalUnions();
  }, []);

  // Fetch organizers
  useEffect(() => {
    const fetchOrganizers = async () => {
      try {
        const organizersData = await lookupService.getOrganizers();
        const formattedOrganizers = organizersData
          .filter(organizer => organizer.role?.roleName === 'organizer')
          .map(organizer => ({
            id: organizer.id.toString(),
            name: organizer.name
          }));
        setOrganizers(formattedOrganizers);
      } catch (error) {
        console.error('Error fetching organizers:', error);
      }
    };

    fetchOrganizers();
  }, []);

  // Fetch sectors
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const sectorsData = await lookupService.getSectors();
        const formattedSectors = sectorsData.map(sector => ({
          id: sector.id.toString(),
          name: sector.name
        }));
        setSectors(formattedSectors);
      } catch (error) {
        console.error('Error fetching sectors:', error);
      }
    };

    fetchSectors();
  }, []);

  // Fetch trades (only those seen in jobsite visits)
  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesData = await lookupService.getTradesFromVisits();
        const formattedTrades = tradesData.map(trade => ({
          id: trade.id.toString(),
          name: trade.name
        }));
        setTrades(formattedTrades);
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };

    fetchTrades();
  }, []);

  // Fetch provinces (filtered by user role on backend)
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const provincesData = await lookupService.getProvinces();
        const formattedProvinces = provincesData.map(province => ({
          id: province.id.toString(),
          name: province.name
        }));
        setProvinces(formattedProvinces);
      } catch (error) {
        console.error('Error fetching provinces:', error);
      }
    };

    fetchProvinces();
  }, []);

  // Effect to update selected contractors when loaded
  useEffect(() => {
    if (contractors.length > 0 && activeFilters.contractor.length > 0) {
        setSelectedContractors(activeFilters.contractor);
    }
  }, [contractors, activeFilters.contractor]);

  // Effect to update selected mechanical contractors when loaded
  useEffect(() => {
    if (mechanicalContractors.length > 0 && activeFilters.mechanicalContractor.length > 0) {
        setSelectedMechanicalContractors(activeFilters.mechanicalContractor);
    }
  }, [mechanicalContractors, activeFilters.mechanicalContractor]);

  // Effect to update selected local unions when loaded
  useEffect(() => {
    if (localUnions.length > 0 && activeFilters.localUnion.length > 0) {
        setSelectedLocalUnions(activeFilters.localUnion);
    }
  }, [localUnions, activeFilters.localUnion]);

  // Effect to update selected values when organizers are loaded
  useEffect(() => {
    if (organizers.length > 0 && activeFilters.organizer.length > 0) {
      setSelectedOrganizers(activeFilters.organizer);
    }
  }, [organizers, activeFilters.organizer]);

  // Effect to update selected values when sectors are loaded
  useEffect(() => {
    if (sectors.length > 0 && activeFilters.sector.length > 0) {
           setSelectedSectors(activeFilters.sector);
    }
  }, [sectors, activeFilters.sector]);

  // Effect to update selected values when trades are loaded
  useEffect(() => {
    if (trades.length > 0 && activeFilters.trade.length > 0) {
      setSelectedTrades(activeFilters.trade);
    }
  }, [trades, activeFilters.trade]);

  // Effect to update selected provinces when loaded
  useEffect(() => {
    if (provinces.length > 0 && activeFilters.province.length > 0) {
      setSelectedProvinces(activeFilters.province);
    }
  }, [provinces, activeFilters.province]);

  // Add useEffect to handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleSection = (section: 'status' | 'contractor' | 'mechanicalContractor' | 'localUnion' | 'organizer' | 'sector' | 'trade' | 'workersType' | 'province') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Update contractor selection handler
  const handleContractorSelect = (selectedIds: string[]) => {
    setSelectedContractors(selectedIds);
  };

  // Update mechanical contractor selection handler
  const handleMechanicalContractorSelect = (selectedIds: string[]) => {
    setSelectedMechanicalContractors(selectedIds);
  };

  // Update local union selection handler
  const handleLocalUnionSelect = (selectedIds: string[]) => {
    setSelectedLocalUnions(selectedIds);
  };

  // Update province selection handler
  const handleProvinceSelect = (selectedIds: string[]) => {
    setSelectedProvinces(selectedIds);
  };

  // Update organizer selection handler
  const handleOrganizerSelect = (selectedIds: string[]) => {
    setSelectedOrganizers(selectedIds);
  };

  // Update sector selection handler
  const handleSectorSelect = (checked: boolean, sectorId: string) => {
    setSelectedSectors(prev => {
      if (checked) {
        return [...prev, sectorId];
      } else {
        return prev.filter(id => id !== sectorId);
      }
    });
  };

  // Update trade selection handler
  const handleTradeSelect = (checked: boolean, tradeId: string) => {
    setSelectedTrades(prev => {
      if (checked) {
        return [...prev, tradeId];
      } else {
        return prev.filter(id => id !== tradeId);
      }
    });
  };

  // Update reset function
  const resetFilters = () => {
    setSelectedStatus([]);
    setSelectedContractors([]);
    setSelectedMechanicalContractors([]);
    setSelectedLocalUnions([]);
    setSelectedProvinces([]);
    setSelectedOrganizers([]);
    setSelectedSectors([]);
    setSelectedTrades([]);
    setSelectedWorkersType([]);
  };

  // Handle clear all
  const handleClearAll = () => {
    resetFilters();
    onApply({
      status: [],
      contractor: [],
      mechanicalContractor: [],
      localUnion: [],
      province: [],
      organizer: [],
      sector: [],
      trade: [],
      workersType: [],
      search: activeFilters.search,
    });
    sessionStorage.removeItem('jobsiteFilters');
    onClose();
  };

  // Update apply filters function to use contractor and sector IDs
  const handleApplyFilters = () => {
    const newFilters = {
      status: selectedStatus,
      contractor: selectedContractors,
      mechanicalContractor: selectedMechanicalContractors,
      localUnion: selectedLocalUnions,
      province: selectedProvinces,
      organizer: selectedOrganizers,
      sector: selectedSectors,
      trade: selectedTrades,
      workersType: selectedWorkersType,
      search: activeFilters.search,
    };
    sessionStorage.setItem('jobsiteFilters', JSON.stringify(newFilters));
    onApply(newFilters);
    onClose();
  };

  // Handle workers type selection
  const handleWorkersTypeSelect = (type: string) => {
    setSelectedWorkersType(prev => {
      if (prev.includes(type)) {
        return prev.filter(item => item !== type);
      } else {
        return [...prev, type];
      }
    });
  };
  return (
    <div
      className={`fixed inset-0 bg-[#A42823] transition-all duration-300 ease-in-out ${isOpen ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0 pointer-events-none'
        } z-50`}
      onClick={e => {
        // Only close if the click is on the overlay itself, not the side panel
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      id="filter-overlay"
    >
      <div
        className={`fixed right-0 bg-white h-svh w-full md:w-[500px] shadow-lg transition-all duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Filter by</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            id="close-filter"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 overflow-y-auto h-[calc(100svh-180px)]">
          <div className="space-y-2">
            {/* Workers Type Section */}
            <Accordion
              title="Workers Type"
              isExpanded={expandedSections.workersType}
              onToggle={() => toggleSection('workersType')}
            >
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={selectedWorkersType.includes('union')}
                  onChange={() => handleWorkersTypeSelect('union')}
                  id="filter-workers-union"
                />
                <span>Union</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={selectedWorkersType.includes('non-union')}
                  onChange={() => handleWorkersTypeSelect('non-union')}
                  id="filter-workers-non-union"
                />
                <span>Non-Union</span>
              </label>
            </Accordion>

            {/* Status Section */}
            <Accordion
              title="Jobsite Status"
              isExpanded={expandedSections.status}
              onToggle={() => toggleSection('status')}
            >
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={selectedStatus.includes('active')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatus([...selectedStatus, 'active']);
                    } else {
                      setSelectedStatus(selectedStatus.filter(s => s !== 'active'));
                    }
                  }}
                  id="filter-status-active"
                />
                <span>Active</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={selectedStatus.includes('inactive')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatus([...selectedStatus, 'inactive']);
                    } else {
                      setSelectedStatus(selectedStatus.filter(s => s !== 'inactive'));
                    }
                  }}
                  id="filter-status-inactive"
                />
                <span>Inactive</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={selectedStatus.includes('completed')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatus([...selectedStatus, 'completed']);
                    } else {
                      setSelectedStatus(selectedStatus.filter(s => s !== 'completed'));
                    }
                  }}
                  id="filter-status-completed"
                />
                <span>Completed</span>
              </label>
            </Accordion>

            {/* General Contractor Section */}
            <Accordion
              title="General Contractor"
              isExpanded={expandedSections.contractor}
              onToggle={() => toggleSection('contractor')}
            >
              <MultiSelectDropdown
                options={contractors}
                onSelect={handleContractorSelect}
                getValue={(option) => option.id}
                getDisplayText={(option) => option.name}
                selectedValues={selectedContractors}
                placeholder="Select general contractors"
                testId="filter-contractor-dropdown"
              />
            </Accordion>

            {/* Mechanical Contractor Section */}
            <Accordion
              title="Mechanical Contractor"
              isExpanded={expandedSections.mechanicalContractor}
              onToggle={() => toggleSection('mechanicalContractor')}
            >
              <MultiSelectDropdown
                options={mechanicalContractors}
                onSelect={handleMechanicalContractorSelect}
                getValue={(option) => option.id}
                getDisplayText={(option) => option.name}
                selectedValues={selectedMechanicalContractors}
                placeholder="Select mechanical contractors"
                testId="filter-mechanical-contractor-dropdown"
              />
            </Accordion>

            {/* Local Union Section */}
            <Accordion
              title="Local Union"
              isExpanded={expandedSections.localUnion}
              onToggle={() => toggleSection('localUnion')}
            >
              <MultiSelectDropdown
                options={localUnions}
                onSelect={handleLocalUnionSelect}
                getValue={(option) => option.id}
                getDisplayText={(option) => option.name}
                selectedValues={selectedLocalUnions}
                placeholder="Select local unions"
                testId="filter-local-union-dropdown"
              />
            </Accordion>

            {/* Province Section */}
            <Accordion
              title="Province"
              isExpanded={expandedSections.province}
              onToggle={() => toggleSection('province')}
            >
              <MultiSelectDropdown
                options={provinces}
                onSelect={handleProvinceSelect}
                getValue={(option) => option.id}
                getDisplayText={(option) => option.name}
                selectedValues={selectedProvinces}
                placeholder="Select provinces"
                testId="filter-province-dropdown"
              />
            </Accordion>

            {/* Trades Section */}
            <Accordion
              title="Trades"
              isExpanded={expandedSections.trade}
              onToggle={() => toggleSection('trade')}
            >
              {trades.map((trade) => (
                <label key={trade.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedTrades.includes(trade.id)}
                    onChange={(e) => handleTradeSelect(e.target.checked, trade.id)}
                    id={`filter-trade-${trade.id}`}
                  />
                  <span>{trade.name}</span>
                </label>
              ))}
            </Accordion>

            {/* Sector Section */}
            <Accordion
              title="Sector"
              isExpanded={expandedSections.sector}
              onToggle={() => toggleSection('sector')}
            >
              {sectors.map((sector) => (
                <label key={sector.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedSectors.includes(sector.id)}
                    onChange={(e) => handleSectorSelect(e.target.checked, sector.id)}
                    id={`filter-sector-${sector.id}`}
                  />
                  <span>{sector.name}</span>
                </label>
              ))}
            </Accordion>
          </div>
        </div>

        {/* Fixed Bottom Buttons */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
          <div className="flex items-center justify-end gap-3">
            <button
              className="btn-secondary"
              onClick={handleClearAll}
              id="filter-clear-all"
            >
              Clear All
            </button>
            <button
              className="btn-primary"
              onClick={handleApplyFilters}
              id="filter-apply"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterOverlay; 